import axios, {type AxiosError} from 'axios';
import Alert from 'helpers/Alert';
import {adjustmentHandler, adjustmentSender} from 'helpers/UnitsFormatHandler';
import {useEffect, useState} from 'react';

import {inputMenuActionType, type ITTTVersionEnum, methodType, SWRKeyEnum} from 'interfaces';

import {useInputMenuContext} from 'context/InputMenu';
import useIsMounted from 'hooks/useIsMounted';
import backendCommunicator from 'services/backendCommunicator';

import {EMPTY_ID, useGetGrowthModel} from 'hooks/swr';
import useParseURL from 'hooks/useParseURL';
import {mutate} from 'swr';

import {GrowthModelResponseAdjustmentTypeEnum, GrowthModelResponseTypeEnum, type GrowthModelResponse} from 'client';
import {getBaseUrl} from '../../utils/runtimeVars';

const BASE_URL = getBaseUrl('');

const DEFAULT_ADJUSTMENT = [0];

type DeepNonNullable<T> = {
  [K in keyof T]: Exclude<T[K], null>;
};

type GrowthModelForm = Required<Omit<DeepNonNullable<GrowthModelResponse>, 'id' | 'planId' | 'version'>> & {
  file?: File;
  version?: ITTTVersionEnum;
};

function useAddEditGrowthModel() {
  const isMounted = useIsMounted();
  const [createUpdateGrowthModel, setCreateUpdateGrowthModel] = useState<boolean>(false);
  const {planId} = useParseURL();

  const inputMenuContext = useInputMenuContext();
  const inputMenuDispatch = inputMenuContext?.dispatch;
  const growthModelId = inputMenuContext?.state.formData?.id;
  const method = inputMenuContext?.state.method;

  const {data} = useGetGrowthModel({resourceId: growthModelId ? [planId, growthModelId] : undefined});

  const [growthModel, setGrowthModel] = useState<GrowthModelForm>({
    name: '',
    type: GrowthModelResponseTypeEnum.EgiSalmon,
    adjustment: [...DEFAULT_ADJUSTMENT],
    adjustmentType: GrowthModelResponseAdjustmentTypeEnum.Constant,
    isDefault: false,
  });

  useEffect(() => {
    if (data) {
      setGrowthModel({
        ...data,
        adjustment: data.adjustment.map(adjustmentHandler) ?? [...DEFAULT_ADJUSTMENT],
        adjustmentType: data.adjustmentType,
        version: data.version ? (data.version as ITTTVersionEnum) : undefined,
      });
    }
  }, [data]);

  const communicationSuccess = () => {
    if (isMounted.current) {
      void mutate([planId, SWRKeyEnum.GET_PLAN_GROWTH_MODELS]);
      void mutate([EMPTY_ID, SWRKeyEnum.GET_GLOBAL_GROWTH_MODELS]);
      inputMenuDispatch && inputMenuDispatch({type: inputMenuActionType.closeMenu});
    }
  };

  useEffect(() => {
    if (!createUpdateGrowthModel || !growthModel) {
      return;
    }
    method &&
      sendGrowthModel({
        growthModel,
        method,
        planId,
        growthModelId,
      })
        .then(() => {
          if (isMounted.current) {
            communicationSuccess();
          }
        })
        .catch((error: AxiosError) => {
          isMounted.current && Alert.error(error);
        })
        .finally(() => {
          isMounted.current && setCreateUpdateGrowthModel(false);
        });
  }, [createUpdateGrowthModel]);

  return {
    createUpdateGrowthModel,
    setCreateUpdateGrowthModel,
    growthModel,
    setGrowthModel,
    method,
    growthModelId,
  };
}
export default useAddEditGrowthModel;

const sendGrowthModel = async ({
  growthModel,
  method,
  planId,
  growthModelId,
}: {
  growthModel: GrowthModelForm;
  method: methodType;
  planId: string;
  growthModelId?: string;
}) => {
  const adjustment = growthModel.adjustment?.map(adjustmentSender);
  if (!adjustment) {
    return await Promise.reject(new Error('Growth model type is required'));
  }

  if (method === methodType.edit && growthModelId) {
    return await backendCommunicator.updateGrowthModel({
      growthModelId,
      planId,
      adjustment,
      name: growthModel.name,
      adjustmentType: growthModel.adjustmentType,
      isDefault: growthModel.isDefault,
      version: growthModel.version ?? String(null),
      ...(growthModel.file && {file: growthModel.file}),
    });
  } else {
    if (!growthModel.type) {
      return await Promise.reject(new Error('Growth model type is required'));
    }
    // FormData workaround
    const requestPayload = new FormData();
    requestPayload.append('planId', planId);
    requestPayload.append('name', growthModel.name);
    requestPayload.append('type', growthModel.type);
    requestPayload.append('adjustmentType', growthModel.adjustmentType);
    requestPayload.append('isDefault', String(growthModel.isDefault));
    requestPayload.append('version', growthModel.version ?? String(null));
    if (growthModel.file) {
      requestPayload.append('file', growthModel.file);
    }
    adjustment?.forEach((adjustment) => {
      requestPayload.append('adjustment', `${adjustment}`);
    });

    return await axios.request<GrowthModelResponse>({
      url: `${BASE_URL}/growthModels/`,
      method: 'POST',
      data: requestPayload,
      headers: {'Content-Type': 'multipart/form-data'},
    });
  }
};
